'use client'

import { useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronRight, ChevronLeft, Instagram, Phone, Mail, MapPin, ChevronUp, ChevronDown, Clock, CreditCard, CalendarIcon, RefreshCcw } from 'lucide-react'
import { ButtonV2 } from '../../components/ButtonV2/ButtonV2'
import { InputV2 } from '../../components/InputV2/InputV2'
import { LabelV2 } from '../../components/LabelV2/LabelV2'
import { CardContent, CardDescription, CardFooter, CardHeader, CardTitle, CardV2 } from '../../components/CardV2/CardV2'
import { TextareaV2 } from '../../components/TextareaV2/TextareaV2'
import { RadioGroup, RadioGroupItem } from '../../components/RadioGroup/RadioGroup'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import useBookingData from '../../hooks/useBookingData'
import BookingAPI, { BookingPlanDto, PendingBookingDto, PendingBookStatus, BookingDetailsDto } from '../../api/BookingAPI'
import { Badge } from '../../components/Badge/Badge'
import { isValidEmail } from '../../shared/validation'
import { BookingFeedback } from '../../components/BookingFeedback/BookingFeedback'
import { BookingSlotUnavailable } from '../../components/BookingSlotUnavailable/BookingSlotUnavailable'
import { Popover, PopoverContent, PopoverTrigger } from '../../components/Popover/Popover'
import { cn } from '../../lib/utils'
import { format, isBefore } from 'date-fns'
import { Calendar } from '../../components/Calendar/Calendar'
import { Alert } from '../../components/Alert/Alert'
import PhoneNumberInput from '../../components/PhoneNumberInput/PhoneNumberInput'
import CONFIG from '../../config/config'
import parseISO from 'date-fns/parseISO'
import startOfToday from 'date-fns/startOfToday'
import { Skeleton } from '../../components/Skeleton/Skeleton'
import FlyoutMenu from '../../components/FlyoutMenu/FlyoutMenu'
import { Icon } from '../../components/Icon/Icon'
import { ContactInfoItem } from '../../components/ContactInfoItem/ContactInfoItem'
import { useTranslation } from 'react-i18next'
import { isSupportedLanguage, languageToLocale } from '../../shared/supported-languages'
import { weekDayToTranslationKey } from '../../shared/weekDayToTranslationKey'
import { datadogRum } from '@datadog/browser-rum';
import { enUS, es, de, it, pl, ptBR, uk } from "date-fns/locale"
import { useLanguageDetection } from '../../hooks/useLanguageDetection'
import { useSourceDetection } from '../../hooks/useSourceDetection'
import { NumberCounter } from '../../components/NumberCounter/NumberCounter'
import { SelectV3, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../components/SelectV3/SelectV3'

type AvailabilityDay = {
  day: string;
  hours: string[];
};

type AvailabilityResponse = {
  availableDays: AvailabilityDay[];
};
type BookingSubmitState = 'success' | 'failure';
type BookingSubmitAfterState = BookingSubmitState | undefined;

const MAX_TEXT_LENGTH = 500;
const TODAY = new Date()
TODAY.setHours(0, 0, 0, 0);

const localeMapping: Record<string, Locale> = {
  "en": enUS,
  "en-US": enUS,
  "es": es,
  "es-ES": es,
  "de": de,
  "de-DE": de,
  "it": it,
  "it-IT": it,
  "pl": pl,
  "pl-PL": pl,
  "pt": ptBR,
  "pt-PT": ptBR,
  "uk": uk,
  "uk-UA": uk,
}

export default function OnlineBookingsPage() {
  const { t, i18n } = useTranslation();
  const { bookingWidgetId } = useParams();
  const { bookingData, loading, submitBooking, submitting } = useBookingData(bookingWidgetId);
  const { changeLanguage } = useLanguageDetection(bookingData?.language);
  const { source } = useSourceDetection();
  const locale = localeMapping[i18n.language] || enUS

  const [step, setStep] = useState(1)
  const [selectedMeeting, setSelectedMeeting] = useState<BookingPlanDto | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [availableTimes, setAvailableTimes] = useState<string[]>([])
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedOffer, setSelectedOffer] = useState<number | null>(null)
  const [expandedOffer, setExpandedOffer] = useState<number | null>(null)
  const [bookingConfirmed, setBookingConfirmed] = useState<BookingSubmitAfterState>();
  const [bookingFailureReason, setBookingFailureReason] = useState<string>();
  const [actualBookingResponse, setActualBookingResponse] = useState<BookingDetailsDto | null>(null);
  const [noDataFound, setNoDataFound] = useState(false)
  const [currencySymbol, setCurrencySymbol] = useState('€')
  const [availability, setAvailability] = useState<AvailabilityResponse>({ availableDays: [] })
  const [availableDates, setAvailableDates] = useState<Date[]>([])
  const [initialLoading, setInitialLoading] = useState(true)
  const [backgroundImageUrl, setBackgroundImageUrl] = useState<string | null>(null)

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [budgetRange, setBudgetRange] = useState('');
  const [numberOfGuests, setNumberOfGuests] = useState(0);
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [size, setSize] = useState('');
  const [weddingDate, setWeddingDate] = useState('');
  const [requestWhereHeardFrom, setRequestWhereHeardFrom] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false)

  const [minWeddingDate, setMinWeddingDate] = useState('')
  const [maxWeddingDate, setMaxWeddingDate] = useState('')
  const [additionalNotesError, setAdditionalNotesError] = useState('');

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const navigate = useNavigate();
  const location = useLocation();
  const [, setProcessingPayment] = useState(false);
  const [, setSubmitting] = useState(false);

  const [pollingPaymentStatus, setPollingPaymentStatus] = useState(false);
  const [pollingAttempts, setPollingAttempts] = useState(0);
  const MAX_POLLING_ATTEMPTS = 12; // 60 seconds total (12 * 5 seconds)
  const POLLING_INTERVAL = 10000; // 10 seconds

  const formatDuration = (duration: string | number): string => {
    const durationNumber = typeof duration === 'number' ? duration : parseInt(duration, 10);
    return `${durationNumber} ${t("bookings.form.step1.appointment.minutes")}`;
  };

  useEffect(() => {
    const today = new Date()
    const maxDate = new Date(today.getFullYear() + 5, 11, 31, 23, 59, 59, 999)
    
    setMinWeddingDate(today.toISOString().split('T')[0])
    setMaxWeddingDate(maxDate.toISOString().split('T')[0])
  }, []);

  const formatFee = (offer: any): string => {
    if (offer.requiresFee) {
      if (offer.feeIsDeposit) {
        if (offer.totalPrice) {
          const remaining = Math.max(0, (offer.totalPrice || 0) - (offer.fee || 0));
          return `${t("bookings.listing.deposit_remaining", { deposit: `${offer.feeCurrency} ${offer.fee}`, remaining: `${offer.totalPriceCurrency || offer.feeCurrency} ${remaining}` })}`;
        }
        return `${t("bookings.listing.deposit", { amount: `${offer.feeCurrency} ${offer.fee}` })}`;
      }
      return `${offer.feeCurrency} ${offer.fee}`;
    }
    if (offer.requiresRefundableFee) {
      return `${offer.refundableFeeCurrency} ${offer.refundableFee} (${t("bookings.listing.refundable")})`;
    }
    return t("bookings.listing.free");
  };

  const languages = CONFIG.SUPPORTED_LANGUAGES.map(l => {
    return {
      code: l.code,
      name: '',
      onClick: () => changeLanguage(l.code),
      icon: <Icon url={l.icon} cssStyles="w-5 rounded h-3"/>,
    }
  })

  const languageMenuItems: any[] = 
    languages.map((lang) => ({
      text: lang.name,
      onClick: () => changeLanguage(lang.code),
      isDisabled: false,
      icon: lang.icon,
    }))
    ;

  useEffect(() => {
    if (bookingData?.currency) {
      setCurrencySymbol(CONFIG.SUPPORTED_CURRENCIES_SYMBOLS[bookingData?.currency as keyof typeof CONFIG.SUPPORTED_CURRENCIES_SYMBOLS]);
    }
  }, [bookingData?.currency])

  useEffect(() => {
    setBackgroundImageUrl(bookingData?.backgroundImageUrl || null);
  }, [bookingData?.backgroundImageUrl])

  useEffect(() => {
    if (selectedMeeting) {
      const currentDate = new Date();
      fetchAvailability(currentDate.getFullYear(), currentDate.getMonth() + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMeeting]);

  useEffect(() => {
    if (!loading) {
      setInitialLoading(false)
    }
  }, [loading])

  useEffect(() => {
    if (!loading && (!bookingData || bookingData?.plans?.length === 0)) {
      setNoDataFound(true);
      return;
    }
    setNoDataFound(false);
  }, [loading, bookingData])

  const toggleOfferExpansion = (id: number) => {
    setExpandedOffer(expandedOffer === id ? null : id)
  }

  const nextStep = () => setStep(step + 1)
  const prevStep = () => setStep(step - 1)

  useEffect(() => {
    if (step !== 2) setCalendarOpen(false);
  }, [step]);

  // Update the useEffect that checks status
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const reservationId = localStorage.getItem('pendingReservationId');

    // If we have a session ID, it means we've returned from payment
    if (sessionId && !pollingPaymentStatus && bookingConfirmed === undefined) {
      // First check if we have the reservation ID stored
      if (reservationId) {
        setPollingPaymentStatus(true);
        setStep(3); // Move to the confirmation step
        pollReservationStatus(reservationId);
      } else {
        // If we somehow lost the reservation ID, show an error
        setBookingConfirmed('failure');
        setBookingFailureReason('booking.system_error');
      }
    }
    // If we just have a reservation ID, check directly
    else if (reservationId && bookingConfirmed === undefined) {
      pollReservationStatus(reservationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, pollingPaymentStatus, bookingConfirmed, bookingWidgetId]);

  // Unified polling function that checks reservation status including payment
  const pollReservationStatus = async (reservationId: string) => {
    try {
      // If we've exceeded maximum attempts, show timeout message
      if (pollingAttempts >= MAX_POLLING_ATTEMPTS) {
        setPollingPaymentStatus(false);
        setBookingConfirmed('failure');
        setBookingFailureReason('booking.payment.timeout');
        return;
      }

      // Call the API to check booking details including payment status
      const response = await BookingAPI.getReservationDetails(bookingWidgetId!, reservationId);

      // Check payment status if it exists
      if (response.data?.paymentStatus) {
        const paymentStatus = response.data.paymentStatus.status;

        if (paymentStatus === 'SUCCEEDED') {
          // Payment successful
          setBookingConfirmed('success');
          setPollingPaymentStatus(false);
          return;
        } else if (paymentStatus === 'FAILED' || paymentStatus === 'DISPUTED' || paymentStatus === 'CANCELED') {
          // Payment failed
          setBookingConfirmed('failure');
          setBookingFailureReason(response.data.paymentStatus.reason || 'booking.payment.failed');
          setPollingPaymentStatus(false);
          return;
        } else if (response.data.paymentStatus.reason) {
          // Explicit payment error returned from API
          setBookingConfirmed('failure');
          setBookingFailureReason(response.data.paymentStatus.reason || 'booking.payment.processing_error');
          setPollingPaymentStatus(false);
          return;
        }
      }

      // Check booking status
      if (response.data?.pendingBooking?.status === 'APPROVED') {
        // Booking is approved
        setBookingConfirmed('success');
        setPollingPaymentStatus(false);
        return;
      } else if (response.data?.pendingBooking?.status === 'REJECTED' ||
                 response.data?.pendingBooking?.status === 'CANCELED_BY_BRIDE') {
        // Booking was cancelled or rejected
        setBookingConfirmed('failure');
        setBookingFailureReason('booking.canceled');
        setPollingPaymentStatus(false);
        return;
      }

      // If we're still here, status is pending/processing - continue polling
      setPollingAttempts(prev => prev + 1);
      setTimeout(() => pollReservationStatus(reservationId), POLLING_INTERVAL);

    } catch (error) {
      console.error("Error checking reservation status:", error);
      setPollingAttempts(prev => prev + 1);
      setTimeout(() => pollReservationStatus(reservationId), POLLING_INTERVAL);
    }
  };

  const handleSubmit = async () => {
    const language = i18n.language;
    const locale = languageToLocale(language);

    if (validateForm()) {
      const customerData: { [key: string]: string } = {
        firstName,
        lastName,
        email,
        phone,
        budgetRange,
        numberOfGuests: numberOfGuests.toString(),
        size,
        weddingDate,
        additionalNotes,
        requestWhereHeardFrom,
        source: source,
      };
  
      const year = selectedDate?.getFullYear();
      const month = String((selectedDate?.getMonth() ?? 0) + 1).padStart(2, '0');
      const day = String(selectedDate?.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      
      // Convert time to HH:mm format
      const timeParts = selectedTime.split(' ');
      let [hours, minutes] = timeParts[0].split(':');
      if (timeParts[1] === 'PM' && hours !== '12') {
        hours = (parseInt(hours, 10) + 12).toString();
      } else if (timeParts[1] === 'AM' && hours === '12') {
        hours = '00';
      }
      const formattedTime = `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  
      try {
        const availabilityCheck = await BookingAPI.availability(
          selectedMeeting?.id!,
          selectedDate!.getFullYear(),
          selectedDate!.getMonth() + 1
        );
        
        const availabilityData: AvailabilityResponse = availabilityCheck.data;
        const selectedDayAvailability = availabilityData?.availableDays?.find(
          (day: AvailabilityDay) => day.day === formattedDate
        );
        
        if (!selectedDayAvailability || !selectedDayAvailability.hours || selectedDayAvailability.hours.length === 0) {
          setBookingConfirmed('failure');
          setBookingFailureReason('booking.feedback.failure.no_longer_available');
          setStep(3);
          datadogRum.addAction('Booking Failed - Slot Unavailable');
          return;
        }
        
        const isTimeStillAvailable = selectedDayAvailability.hours.includes(selectedTime);
        if (!isTimeStillAvailable) {
          setBookingConfirmed('failure');
          setBookingFailureReason('booking.feedback.failure.no_longer_available');
          setStep(3);
          datadogRum.addAction('Booking Failed - Slot Unavailable');
          return;
        }
      } catch (error) {
        console.warn('Availability re-check failed, continuing with submission:', error);
      }
  
      const bookingPayload: PendingBookingDto = {
        bookingPlanId: selectedMeeting?.id || 0,
        appointmentDate: formattedDate,  // Formatted as YYYY-MM-DD
        appointmentTime: formattedTime,  // Formatted as HH:mm
        customerData,
      };
  
      try {
        datadogRum.addAction('Submit Booking');
        setSubmitting(true);

        const hasFees = selectedMeeting?.requiresFee || selectedMeeting?.requiresRefundableFee;
        const useOnlinePayments = selectedMeeting?.useOnlinePayments !== false;
        
        if (hasFees && useOnlinePayments) {
          setProcessingPayment(true);

          // Request payment session - this now creates the booking first
          const paymentResponse = await BookingAPI.createPaymentSession(bookingWidgetId!, bookingPayload, locale);

          if (paymentResponse.error) {
            // Handle payment error more explicitly
            setBookingConfirmed('failure');
            setBookingFailureReason(paymentResponse.reason || 'booking.payment.failed');
            setStep(3);
            setProcessingPayment(false);
            datadogRum.addAction('Booking Failed - Payment Error');
            return;
          }

          // If payment is required, redirect to payment
          if (paymentResponse.requiresPayment === "true" && paymentResponse.url) {
            // No need to store in localStorage anymore
            window.location.href = paymentResponse.url;
            return; // Stop execution - user is being redirected
          } else if (paymentResponse.reservationId) {
            // No payment required, redirect to the booking details page
            navigate(`/bookings/${bookingWidgetId}/${paymentResponse.reservationId}?autoRefresh=true`);
            return;
          }
        } else {
          // No payment functionality needed, submit booking directly
          const response = await submitBooking(bookingWidgetId!, bookingPayload, locale);
          if (!response?.success) {
            setBookingConfirmed('failure');
            setBookingFailureReason(response?.reason);
            setStep(3); // Show error in current page
            datadogRum.addAction('Booking Failed - Submission Error');
          } else {
            setActualBookingResponse(response);
            
            // Booking successful - navigate to booking details page
            const reservationId = response?.pendingBooking?.reservationReference;
            if (reservationId) {
              navigate(`/bookings/${bookingWidgetId}/${reservationId}?autoRefresh=true`);
              return;
            } else {
              // Fallback if no reservationId is returned
              setBookingConfirmed('success');
              setStep(3);
            }
          }
        }
      } catch (error: any) {
        setBookingConfirmed('failure');
        setBookingFailureReason(error.message || 'booking.system_error');
        console.error("Booking submission failed", error);
        setStep(3); // Show error in current page
        datadogRum.addAction('Booking Failed - Submission Error');
      } finally {
        setProcessingPayment(false);
        setSubmitting(false);
      }
    } else {
      const firstErrorField = document.querySelector('.border-red-500');
      if (firstErrorField) {
        (firstErrorField as HTMLElement).scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  useEffect(() => {
    return () => {
      localStorage.removeItem('pendingReservationId');
      localStorage.removeItem('paymentSessionId');
    };
  }, []);

  useEffect(() => {
    if (step === 1) {
      setActualBookingResponse(null);
    }
  }, [step]);

  const handleWeddingDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWeddingDate(e.target.value)
    setErrors(prev => ({ ...prev, weddingDate: '' }))
  }

  const validateWeddingDate = (date: string): boolean => {
    return date >= minWeddingDate && date <= maxWeddingDate
  }

  const formatDate = (inputDate: Date) => {
    const date = new Date(inputDate)
    if (isNaN(date.getTime())) {
      return '';
    }
    
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    
    return `${year}-${month}-${day}`;
  }

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date) {
      const formattedDate = format(date, 'yyyy-MM-dd');
      const selectedDayAvailability = availability.availableDays.find(day => day.day === formattedDate);
      setAvailableTimes(selectedDayAvailability?.hours || []);
      setSelectedTime('');
    } else {
      setAvailableTimes([]);
    }
    setSelectedTime('');
    setCalendarOpen(false);
    
    // Clear date error when user selects a date
    if (date && errors.selectedDate) {
      setErrors(prev => ({ ...prev, selectedDate: '' }));
    }
  };

  // TODO: use memo
  const fetchAvailability = async (year: number, month: number) => {
    try {
      const response = await BookingAPI.availability(selectedMeeting?.id!, year, month);
      const data: AvailabilityResponse = response.data;
      setAvailability(data);
      setAvailableDates(data.availableDays.map(day => parseISO(day.day)));
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};
  
    if (selectedMeeting?.requestName) {
      if (!firstName.trim()) {
        newErrors.firstName = 'booking.form.required';
      }
      if (!lastName.trim()) {
        newErrors.lastName = 'booking.form.required';
      }
    }
  
    if (selectedMeeting?.requestEmail) {
      if (!email.trim()) {
        newErrors.email = 'booking.form.required';
      } else if (!isValidEmail(email)) {
        newErrors.email = 'booking.form.error.email';
      }
    }
  
    if (selectedMeeting?.requestPhone) {
      if (!phone.trim()) {
        newErrors.phone = 'booking.form.required';
      } else if (!/^\+?\d{10,15}$/.test(phone)) {
        newErrors.phone = 'booking.form.error.phone';
      }
    }
  
    if (selectedMeeting?.requestBudget) {
      if (!budgetRange.trim()) {
        newErrors.budgetRange = 'booking.form.required';
      }
    }
  
    if (selectedMeeting?.requestSize) {
      if (!size.trim()) {
        newErrors.size = 'booking.form.required';
      }
    }
  
    if (selectedMeeting?.requestWeddingDate) {
      if (!weddingDate.trim()) {
        newErrors.weddingDate = 'booking.form.required';
      } else if (!validateWeddingDate(weddingDate)) {
        newErrors.weddingDate = 'booking.form.error.date_range';
      }
    }

    if (selectedMeeting?.requestNumberOfPeople) {
      if (numberOfGuests < 0) {
        newErrors.numberOfGuests = 'booking.form.required';
      }
    }

    if (selectedMeeting?.requestWhereHeardFrom) {
      if (!requestWhereHeardFrom.trim()) {
        newErrors.requestWhereHeardFrom = 'booking.form.required'
      }
    }
  
    if (!selectedOffer) {
      newErrors.selectedOffer = 'booking.form.required';
    }
  
    if (!selectedDate) {
      newErrors.selectedDate = 'booking.form.required';
    }
  
    if (!selectedTime) {
      newErrors.selectedTime = 'booking.form.required';
    }

    if (selectedMeeting?.allowSpecialComments && additionalNotes.length > MAX_TEXT_LENGTH) {
      newErrors.additionalNotes = 'booking.form.error.notes_too_long';
    }
  
    setErrors(newErrors);
  
    return Object.keys(newErrors).length === 0;
  }

  const handleSelectMeeting = (offerId: number) => {
    const selectedChoosablePlan = bookingData?.plans?.find((m) => m.id === offerId) || null;
    setSelectedMeeting(selectedChoosablePlan);
    setSelectedOffer(offerId);
    setSelectedDate(undefined);
    setSelectedTime('');
    setAvailableTimes([]);
    setAvailability({ availableDays: [] });
    setAvailableDates([]);
  };

  const handleSelectOffer = (value: string) => { 
    const offerId = parseInt(value); 
    handleSelectMeeting(offerId);
  }

  const budgetRanges = useMemo(() => {
    if (selectedMeeting?.budgetRanges && selectedMeeting.budgetRanges.length > 0) {
      return selectedMeeting.budgetRanges.map(range => ({
        value: `${range.min}-${range.max}`,
        label: `${range.min} - ${range.max}`
      }));
    }
    return [
      { value: '500-1000', label: `500 - 1,000` },
      { value: '1000-5000', label: `1,000 - 5,000` },
      { value: '5000-10000', label: `5,000 - 10,000` },
      { value: '10000-15000', label: `10,000 - 15,000` },
      { value: '15000+', label: `15,000+` },
    ];
  }, [selectedMeeting]);

  const heardFromOptions = useMemo(() => ([
    { key: "booking.form.step3.heard_from.walked_past", value: "WALK_IN" },
    { key: "booking.form.step3.heard_from.instagram", value: "INSTAGRAM" },
    { key: "booking.form.step3.heard_from.tiktok", value: "TIKTOK" },
    { key: "booking.form.step3.heard_from.facebook", value: "FACEBOOK" },
    { key: "booking.form.step3.heard_from.former_bride", value: "WORD_OF_MOUTH" },
    { key: "booking.form.step3.heard_from.store_partner", value: "SHOW" },
    { key: "booking.form.step3.heard_from.other", value: "GOOGLE" }
  ]), [])

  const renderStep = () => {
    switch(step) {
      case 1:
        return (
          <motion.div key='step-1'
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
          >
          <CardHeader>
            <CardTitle>{t("booking.form.step1.title")}</CardTitle>
            <CardDescription>{t("booking.form.step1.subtitle")}</CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup
              onValueChange={(value) => handleSelectOffer(value)}
              className="space-y-4"
            >
              {bookingData?.plans?.map((offer: any) => (
                <CardV2 key={offer.id} className={`overflow-hidden transition-all duration-200 ${selectedOffer === offer.id ? 'ring-2 ring-primary' : ''}`}>
                  <CardContent className="p-0">
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center space-x-3 flex-grow">
                        <RadioGroupItem value={offer.id.toString()} id={`offer-${offer.id}`} />
                        <LabelV2 htmlFor={`offer-${offer.id}`} className="font-medium cursor-pointer">
                          {offer.name}
                        </LabelV2>
                      </div>
                      <div className="flex items-center space-x-4 flex-shrink-0">
                        {offer.requiresFee && !offer.feeIsDeposit && (
                          <Badge variant="default">
                            {`${offer.feeCurrency} ${offer.fee}`}
                          </Badge>
                        )}
                        {offer.requiresFee && offer.feeIsDeposit && (
                          <div className="flex items-center gap-2">
                            <Badge variant="default">
                              {t("bookings.listing.badge.deposit", { amount: `${offer.feeCurrency} ${offer.fee}` }) || `Deposit ${offer.feeCurrency} ${offer.fee}`}
                            </Badge>
                            {typeof offer.totalPrice === 'number' && (
                              <Badge variant="outline" className="text-muted-foreground">
                                {t("bookings.listing.badge.total", { amount: `${offer.totalPriceCurrency || offer.feeCurrency} ${offer.totalPrice}` }) || `Total ${offer.totalPriceCurrency || offer.feeCurrency} ${offer.totalPrice}`}
                              </Badge>
                            )}
                          </div>
                        )}
                        {!offer.requiresFee && !offer.requiresRefundableFee && (
                          <Badge variant="secondary">
                            {t("bookings.listing.free")}
                          </Badge>
                        )}
                        {offer.requiresRefundableFee && (
                            <Badge variant="outline" className="border-primary text-primary">
                              {t("bookings.listing.refundable")} ({offer.refundableFeeCurrency} {offer.refundableFee})
                            </Badge>
                        )}
                        <ButtonV2
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleOfferExpansion(offer.id)}
                        >
                          {expandedOffer === offer.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </ButtonV2>
                      </div>
                    </div>
                    <AnimatePresence>
                      {expandedOffer === offer.id && (
                        <motion.div
                          key={`offer-${offer.id}`}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="px-4 pb-4"
                        >
                          <div className="text-sm text-muted-foreground space-y-2 max-w-full">
                            <div 
                              className="break-words prose prose-sm max-w-none"
                              dangerouslySetInnerHTML={{ __html: offer.customerInfo || '' }}
                            />
                            <div>
                              {offer.requiresFee && !offer.feeIsDeposit && (
                                <div className="flex items-center">
                                  <CreditCard className="h-4 w-4 mr-1 flex-shrink-0" />
                                  <span>{t("bookings.form.step1.appointment.fee")} ({offer.feeCurrency} {offer.fee})</span>
                                </div>
                              )}
                              {offer.requiresFee && offer.feeIsDeposit && (
                                <div className="flex flex-col">
                                  <div className="flex items-center">
                                    <CreditCard className="h-4 w-4 mr-1 flex-shrink-0" />
                                    <span>{t("bookings.form.step1.appointment.deposit")} ({offer.feeCurrency} {offer.fee})</span>
                                  </div>
                                  {typeof offer.totalPrice === 'number' && (
                                    <div className="ml-5 mt-1 text-xs italic">
                                      {t("bookings.form.step1.appointment.deposit.note", { remaining: `${offer.totalPriceCurrency || offer.feeCurrency} ${Math.max(0, (offer.totalPrice || 0) - (offer.fee || 0))}` })}
                                    </div>
                                  )}
                                </div>
                              )}
                              {offer.requiresRefundableFee && (
                                <>
                                  <div className="flex items-center">
                                    <RefreshCcw className="h-4 w-4 mr-1 flex-shrink-0" />
                                    <span>{t("bookings.form.step1.appointment.refundable_fee")} ({offer.refundableFeeCurrency} {offer.refundableFee})</span>
                                  </div>
                                  <div className="ml-5 mt-1 text-xs italic">
                                    {t("bookings.form.step1.appointment.refundable_fee.note")}
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                              <div className="flex items-center">
                                <Clock className="h-4 w-4 mr-1 flex-shrink-0" />
                                <span>{offer.duration} {t("bookings.form.step1.appointment.minutes")}</span>
                              </div>
                              <div className="flex items-center">
                                <CalendarIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                                <span className="break-words">{offer?.availableDays?.length > 0 ? offer.availableDays.map((ad: number) => t(weekDayToTranslationKey(ad))).join(', ') : t("booking.form.step1.appointment.every_day")}</span>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </CardContent>
                </CardV2>
              ))}
            </RadioGroup>
            {errors.selectedOffer && <p className="text-red-500 text-sm">{t(errors.selectedOffer)}.</p>}
          </CardContent>
        </motion.div>
      )
      case 2:
        return (
          <motion.div key='step-2'
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
          >
            <CardHeader>
              <CardTitle>{t("booking.form.step2.title")}</CardTitle>
              <CardDescription>{t("booking.form.step2.subtitle")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <LabelV2 htmlFor="date">{t("booking.form.step2.date")}</LabelV2>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <ButtonV2
                      id="date"
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !selectedDate && "text-muted-foreground",
                        errors.selectedDate && "border-red-500"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? format(selectedDate, "PPP", { locale }) : <span>{t("booking.form.step2.date.label")}</span>}
                    </ButtonV2>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      disabled={(date: any) => isBefore(date, startOfToday())}
                      modifiers={{
                        disabled: (date: any) => (date <= TODAY || !availableDates.some(d => d.getTime() === date.getTime())),  
                        available: (date: any) => availableDates.some(d => d.getTime() === date.getTime()),
                      }}
                      modifiersStyles={{
                        disabled: {
                          backgroundColor: 'rgb(229 231 235)',
                          borderRadius: '25px',
                          textDecoration: 'line-through',
                          color: 'rgb(107 114 128)',
                          opacity: 0.8,
                          fontWeight: 'normal',
                          textDecorationThickness: '2px',
                          textDecorationColor: 'rgb(156 163 175)'
                        },
                      }}
                      onMonthChange={(date: Date) => {
                        fetchAvailability(date.getFullYear(), date.getMonth() + 1);
                      }}
                      classNames={{
                        chevron: 'rgb(196, 179, 131)'
                      }}
                      locale={locale}
                    />
                  </PopoverContent>
                </Popover>
                {errors.selectedDate && <p className="text-red-500 text-sm">{t(errors.selectedDate)}</p>}
              </div>
              { selectedDate && availableTimes && 
                <div className="space-y-2">
                  <LabelV2 htmlFor="time">{t("booking.form.step2.time")}</LabelV2>
                  <div className={cn("grid grid-cols-3 gap-2", errors.selectedTime && "ring-2 ring-red-500 rounded-md p-1")}>
                    {availableTimes.map((slot) => (
                      <ButtonV2
                        key={slot}
                        variant={selectedTime === slot ? "default" : "outline"}
                        onClick={() => {
                          setSelectedTime(slot);
                          if (errors.selectedTime) {
                            setErrors(prev => ({ ...prev, selectedTime: '' }));
                          }
                        }}
                        disabled={!selectedDate}
                        className={errors.selectedTime ? "border-red-500" : ""}
                      >
                        {slot}
                      </ButtonV2>
                    ))}
                  </div>
                  {errors.selectedTime && <p className="text-red-500 text-sm">{t(errors.selectedTime)}</p>}
                </div>
              }
            </div>
            </CardContent>
          </motion.div>
        )
        case 3:
          return (
            <motion.div key='step-3'
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
            >
              <CardHeader>
                <CardTitle>{t("booking.form.step3.title")}</CardTitle>
                <CardDescription>{t("booking.form.step3.subtitle")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {selectedMeeting?.requestName && (
                  <>
                      <div className="space-y-2 col-span-full">
                        <LabelV2 htmlFor="firstName">{t("booking.form.step3.first_name")}</LabelV2>
                        <InputV2
                          id="firstName"
                          placeholder="Elizabeth"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                            className={`w-full ${errors.firstName ? 'border-red-500' : ''}`}
                          />
                          {errors.firstName && <p className="text-red-500 text-sm">{t(errors.firstName)}.</p>}
                      </div>
                      <div className="space-y-2 col-span-full">
                        <LabelV2 htmlFor="lastName">{t("booking.form.step3.last_name")}</LabelV2>
                        <InputV2
                          id="lastName"
                          placeholder="Bennet"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          className={`w-full ${errors.lastName ? 'border-red-500' : ''}`}
                        />
                        {errors.lastName && <p className="text-red-500 text-sm">{t(errors.lastName)}.</p>}
                      </div>
                    </>
                  )}
                  {selectedMeeting?.requestEmail && (
                    <div className="space-y-2 col-span-full">
                      <LabelV2 htmlFor="email">{t("booking.form.step3.email")}</LabelV2>
                      <InputV2
                        id="email"
                        type="email"
                        placeholder="elizabeth@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`w-full ${errors.email ? 'border-red-500' : ''}`}
                      />
                      {errors.email && <p className="text-red-500 text-sm">{t(errors.email)}.</p>}
                    </div>
                  )}
                  {selectedMeeting?.requestPhone && (
                    <div className="space-y-2 col-span-full">
                      <LabelV2 htmlFor="phone">{t("booking.form.step3.phone")}</LabelV2>
                      <PhoneNumberInput 
                        onChange={c => setPhone(c)}
                        defaultValue={phone}
                        defaultCountry={bookingData?.country}
                      />
                      {errors.phone && <p className="text-red-500 text-sm">{t(errors.phone)}.</p>}
                    </div>
                  )}
                  {selectedMeeting?.requestBudget && (
                  <div className="space-y-2 col-span-2">
                    <LabelV2 htmlFor="budget">{t("booking.form.step3.budget")}</LabelV2>
                    <SelectV3 onValueChange={(value) => setBudgetRange(value)} value={budgetRange}>
                      <SelectTrigger className={errors.budgetRange ? 'border-red-500' : ''}>
                        <SelectValue placeholder={t("booking.form.step3.budget.label")} />
                      </SelectTrigger>
                      <SelectContent>
                        {budgetRanges.map((range) => (
                          <SelectItem key={range.value} value={range.value}>
                            {currencySymbol}{range.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </SelectV3>
                    {errors.budgetRange && <p className="text-red-500 text-sm">{t(errors.budgetRange)}.</p>}
                  </div>
                )}
                {selectedMeeting?.requestSize && (
                  <div className="space-y-2 col-span-2">
                    <LabelV2 htmlFor="size">{t("booking.form.step3.size")}</LabelV2>
                    <InputV2
                      id="size"
                      placeholder={t("booking.form.step3.size.label")!}
                      value={size}
                      onChange={(e) => setSize(e.target.value)}
                      className={errors.size ? 'border-red-500' : ''}
                    />
                    {errors.size && <p className="text-red-500 text-sm">{t(errors.size)}.</p>}
                  </div>
                )}
                {selectedMeeting?.requestWeddingDate && (
                  <div className="space-y-2 col-span-2">
                    <LabelV2 htmlFor="weddingDate">{t("booking.form.step3.wedding_date")}</LabelV2>
                    <InputV2
                      id="weddingDate"
                      type="date"
                      value={weddingDate}
                      onChange={handleWeddingDateChange}
                      min={minWeddingDate}
                      max={maxWeddingDate}
                      className={errors.weddingDate ? 'border-red-500' : ''}
                    />
                    {errors.weddingDate && <p className="text-red-500 text-sm">{t(errors.weddingDate)}: {minWeddingDate} - {maxWeddingDate}</p>}
                  </div>
                )}
                {selectedMeeting?.requestNumberOfPeople && (
                  <div className="space-y-2 col-span-2">
                    <LabelV2 htmlFor="guests">{t("booking.form.step3.no_of_guests")}</LabelV2>
                    <NumberCounter
                      value={numberOfGuests}
                      onChange={setNumberOfGuests}
                      min={0}
                      max={10}
                      placeholder="0"
                      error={!!errors.numberOfGuests}
                    />
                    {errors.numberOfGuests && <p className="text-red-500 text-sm">{t(errors.numberOfGuests)}.</p>}
                  </div>
                )}
                {selectedMeeting?.allowSpecialComments && (
                  <div className="space-y-2 col-span-2">
                    <LabelV2 htmlFor="notes">{t("booking.form.step3.notes")}</LabelV2>
                    <TextareaV2
                      id="notes"
                      placeholder={t("booking.form.step3.notes.label")!}
                      value={additionalNotes}
                      onChange={(e) => {
                        const value = e.target.value;
                        setAdditionalNotes(value);
                        if (value.length > MAX_TEXT_LENGTH) {
                          setAdditionalNotesError(t("booking.form.error.notes_too_long")!);
                        } else {
                          setAdditionalNotesError('');
                        }
                      }}
                    />
                    <p className="text-sm text-gray-500 mt-1">{additionalNotes.length}/{MAX_TEXT_LENGTH} {t("booking.form.step3.characters")}</p> {}
                    {additionalNotesError && <p className="text-red-500 text-sm">{additionalNotesError}</p>}
                  </div>
                )}
                {selectedMeeting?.requestWhereHeardFrom && (
                  <div className="space-y-2 col-span-2">
                    <LabelV2 htmlFor="whereHeardFrom">
                      {t("booking.form.step3.heard_from")}
                    </LabelV2>

                    <SelectV3
                      value={requestWhereHeardFrom}
                      onValueChange={(v) => setRequestWhereHeardFrom(v)}
                    >
                      <SelectTrigger
                        id="whereHeardFrom"
                        className={`w-full ${errors.requestWhereHeardFrom ? "border-red-500" : ""}`}
                      >
                        <SelectValue placeholder={t("booking.form.step3.heard_from.label")} />
                      </SelectTrigger>

                      <SelectContent className="z-[100]" onCloseAutoFocus={(e) => e.preventDefault()}>
                        {heardFromOptions.map((option) => (
                          <SelectItem
                            key={option.key}
                            value={option.value}
                          >
                            {t(option.key)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </SelectV3>

                    {errors.requestWhereHeardFrom && (
                      <p className="text-red-500 text-sm">{t(errors.requestWhereHeardFrom)}.</p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </motion.div>
        )
      default:
        return null
    }
  }

  const renderBookingConfirmation = () => {
    if (pollingPaymentStatus) {
      return (
        <div className="text-center p-8">
          <div className="flex justify-center mb-4">
            <RefreshCcw className="animate-spin h-8 w-8 text-primary" />
          </div>
          <h3 className="text-xl font-semibold mb-2">{t("booking.payment.processing.title")}</h3>
          <p>{t("booking.payment.processing.message")}</p>
          <p className="text-sm text-muted-foreground mt-4">
            {t("booking.payment.processing.wait", { count: MAX_POLLING_ATTEMPTS - pollingAttempts })}
          </p>
        </div>
      );
    }

    if (bookingConfirmed === 'failure' && bookingFailureReason === 'booking.feedback.failure.no_longer_available') {
      return (<BookingSlotUnavailable onRetry={() => window.location.reload()} />);
    }

    if (bookingConfirmed === 'success' || bookingConfirmed === 'failure') {
      let bookingDetailsDto: BookingDetailsDto | undefined;

      if (actualBookingResponse) {
        bookingDetailsDto = actualBookingResponse;
      }
      else if (selectedMeeting) {
        bookingDetailsDto = {
          success: bookingConfirmed === 'success',
          pendingBooking: {
            bookingPlanId: selectedMeeting.id || 0,
            appointmentDate: selectedDate ? formatDate(selectedDate) : '',
            appointmentTime: selectedTime,
            customerData: {
              firstName,
              lastName,
              email,
              phone
            },
            status: bookingConfirmed === 'success' ? 'APPROVED' as PendingBookStatus : 'REJECTED' as PendingBookStatus
          },
          storeDetails: {
            name: bookingData?.storeName || '',
            address: bookingData?.contactInfo?.address || '',
            contactPhone: bookingData?.contactInfo?.phoneNumber || '',
            contactEmail: bookingData?.contactInfo?.email,
            instagram: bookingData?.contactInfo?.instagram,
            logoUrl: bookingData?.logoUrl
          },
          bookingPlan: selectedMeeting,
          canCancel: !!selectedMeeting.brideCanCancel,
          meetingChanges: {}
        };
      }

      return (
        <BookingFeedback
          state={bookingConfirmed === 'success' ? 'success' : 'failure'}
          bookingDetails={bookingDetailsDto}
          storeName={bookingData?.storeName || ''}
          storeAddress={bookingData?.contactInfo?.address || ''}
          contactPhone={bookingData?.contactInfo?.phoneNumber || ''}
          onRetry={() => window.location.reload()}
          onRetryPayment={() => window.location.reload()}
          failureReason={bookingFailureReason}
        />
      );
    }

    return null;
  }

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <CardV2 className="w-full max-w-4xl">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <CardHeader>
                <Skeleton className="h-8 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent className="space-y-4">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </CardContent>
              <CardFooter className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                <Skeleton className="h-10 w-24" />
                <Skeleton className="h-10 w-24" />
              </CardFooter>
            </div>
            <div className="md:col-span-1 bg-gray-50 p-6 rounded-r-lg">
              <div className="space-y-6">
                <div className="text-center">
                  <Skeleton className="w-[100px] h-[100px] mx-auto rounded-full" />
                  <Skeleton className="h-6 w-3/4 mx-auto mt-4" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </div>
            </div>
          </div>
        </CardV2>
      </div>
    )
  }

  if (noDataFound) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <CardV2 className="w-full max-w-4xl">
          <CardHeader>
            <CardTitle>{t("booking.failure.title")}</CardTitle>
            <CardDescription>{t("booking.failure.subtitle")}.</CardDescription>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive" className="mb-4">
              <p>{t("booking.failure.tip")}.</p>
            </Alert>
            <div className="space-y-4">
              <p>{t("booking.failure.try_actions")}:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>{t("booking.failure.try_actions.first")}.</li>
                <li>{t("booking.failure.try_actions.second")}.</li>
                <li>{t("booking.failure.try_actions.third")}.</li>
              </ul>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <ButtonV2 onClick={() => window.location.reload()} className="w-full">
              {t("booking.failure.refresh")}
            </ButtonV2>
          </CardFooter>
          <span className="flex space-x-1 justify-center mb-3">
            <p>{t("booking.form.language")}: </p>
            <FlyoutMenu
              menuItems={languageMenuItems}
              trigger={
                isSupportedLanguage(i18n.language)
                  ? languages.filter(l => l.code === i18n.language)[0]?.icon
                  : languages.filter(l => l.code === CONFIG.DEFAULT_LANGUAGE.code)[0]?.icon
              }
              mobileTrigger={isSupportedLanguage(i18n.language)
                ? languages.filter(l => l.code === i18n.language)[0]?.icon
                : languages.filter(l => l.code === CONFIG.DEFAULT_LANGUAGE.code)[0]?.icon}
            />
          </span>
        </CardV2>
      </div>
    )
  }

  const renderMainCard = () => (
    <CardV2 className="w-full max-w-4xl shadow-xl border-0 bg-white">
      <div className="grid md:grid-cols-3 gap-6">
        { pollingPaymentStatus || bookingConfirmed ?
          <div className="md:col-span-2">
            {renderBookingConfirmation()}
          </div> : 
          <div className="md:col-span-2">
            <AnimatePresence mode="wait">
              {renderStep()}
            </AnimatePresence>
            <CardFooter className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
              <ButtonV2 onClick={prevStep} disabled={step === 1} variant="outline" className="w-full sm:w-auto">
                <ChevronLeft className="mr-2 h-4 w-4" /> {t("booking.form.back")}
              </ButtonV2>
              <span className="flex space-x-1">
                <p>{t("booking.form.language")}: </p>
                <FlyoutMenu
                  menuItems={languageMenuItems}
                  trigger={
                    isSupportedLanguage(i18n.language)
                      ? languages.filter(l => l.code === i18n.language)[0]?.icon
                      : languages.filter(l => l.code === CONFIG.DEFAULT_LANGUAGE.code)[0]?.icon
                  }
                  mobileTrigger={isSupportedLanguage(i18n.language)
                    ? languages.filter(l => l.code === i18n.language)[0]?.icon
                    : languages.filter(l => l.code === CONFIG.DEFAULT_LANGUAGE.code)[0]?.icon}
                />
              </span>
              {step < 3 && (
                  <ButtonV2
                    onClick={() => {
                      if (step === 1 && !selectedOffer) {
                        setErrors((prev) => ({ ...prev, selectedOffer: `${t("bookings.form.step1.error.type")}.` }));
                        return;
                      }
                      if (step === 2 && (!selectedDate || !selectedTime)) {
                        setErrors((prev) => ({
                          ...prev,
                          selectedDate: !selectedDate ? `${t("bookings.form.step1.error.date")}.` : '',
                          selectedTime: !selectedTime ? `${t("bookings.form.step1.error.time")}.` : '',
                        }));
                        return;
                      }
                      setErrors({});
                      nextStep();
                    }}
                    disabled={submitting}
                    className="w-full sm:w-auto"
                  >
                    {t("booking.form.next")} <ChevronRight className="ml-2 h-4 w-4" />
                  </ButtonV2>
              )}
              {step === 3 && (
                <ButtonV2 onClick={handleSubmit} disabled={submitting} className="w-full sm:w-auto">
                  {submitting ? `${t("booking.form.submitting")}...` : t("booking.form.book_appointment")} <ChevronRight className="ml-2 h-4 w-4" />
                </ButtonV2>
              )}
            </CardFooter>
          </div>
        }
        <div className="md:col-span-1 bg-gray-50 p-6 rounded-r-lg">
          <div className="space-y-6">
            <div className="text-center">
              {bookingData?.logoUrl && (
                <img src={bookingData.logoUrl} alt="Store Logo" className="w-[100px] mx-auto rounded-full" />
              )}
              <h2 className="mt-4 text-xl font-semibold">{bookingData?.storeName}</h2>
            </div>
            <div className="space-y-2">
              <ContactInfoItem icon={Phone} value={bookingData?.contactInfo?.phoneNumber} />
              <ContactInfoItem icon={Mail} value={bookingData?.contactInfo?.email} />
              <ContactInfoItem icon={MapPin} value={bookingData?.contactInfo?.address} />
              <ContactInfoItem icon={Instagram} value={bookingData?.contactInfo?.instagram} />
            </div>
            {selectedMeeting && (
              <div className="border-t pt-4">
                <h3 className="font-semibold">{t("booking.confirmation.type")}:</h3>
                <p>{selectedMeeting.name}</p>
                <p className="text-sm text-muted-foreground">
                  {formatDuration(selectedMeeting.duration)} | {formatFee(selectedMeeting)}
                </p>
              </div>
            )}
            {selectedDate && selectedTime && (
              <div className="border-t pt-4">
                <h3 className="font-semibold">{t("booking.confirmation.time")}:</h3>
                <p>{formatDate(selectedDate!)} {t("booking.confirmation.hour_at")} {selectedTime}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </CardV2>
  );

  const hasBackground = !!backgroundImageUrl;
  const backgroundMode = bookingData?.backgroundImageMode || 'BACKGROUND';

  // Case 1: no configured background image at all → plain layout, no hero/header image.
  if (!hasBackground) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4 py-8 md:py-16">
        {renderMainCard()}
      </div>
    )
  }

  // Case 2: background image used as header/hero only.
  if (backgroundMode === 'HEADER') {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="relative w-full h-80 md:h-[360px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url("${backgroundImageUrl}")`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent" />
          <div className="relative z-10 flex h-full items-end justify-center px-4 pb-8">
            <div className="text-center text-white space-y-2">
              {bookingData?.logoUrl && (
                <img
                  src={bookingData.logoUrl}
                  alt="Store Logo"
                  className="h-16 w-16 md:h-20 md:w-20 rounded-full mx-auto border-2 border-white/80 bg-white/80 object-contain"
                />
              )}
              <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
                {bookingData?.storeName}
              </h1>
            </div>
          </div>
        </div>

        <div className="flex justify-center p-4 pb-10 md:pb-16 mt-6 md:mt-10">
          {renderMainCard()}
        </div>
      </div>
    );
  }

  // Case 3: BACKGROUND mode → full-page background layout (current behaviour).
  return (
    <div
      className="min-h-screen relative bg-cover bg-top flex items-center justify-center px-4 py-8 md:py-16"
      style={{
        backgroundImage: `url("${backgroundImageUrl}")`,
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/5" />

      {/* Booking content */}
      <div className="relative z-10 w-full max-w-4xl">
        {renderMainCard()}
      </div>
    </div>
  )
}